/**
 * Demo data for the first-run UI. UI only: nothing here reaches a backend.
 * Developers wire the real sources in later; the shapes are deliberately
 * close to what the app already uses (FUB users, sources, stages, tags).
 */

export const FF_NUMBER = '(719) 800-4343'
export const FF_NUMBER_E164 = '+17198004343'

export interface IPerson {
   id: string
   name: string
   email: string
   phone?: string
   role: 'Owner' | 'Agent' | 'Admin'
}

export const DEMO_OWNER: IPerson = { id: 'u1', name: 'Sam Patel', email: 'sam@patelrealty.com', phone: '(719) 555-0100', role: 'Owner' }

/** Everyone in the connected Follow Up Boss account except the owner. */
export const DEMO_TEAM: IPerson[] = [
   { id: 'u2', name: 'Jordan Lee', email: 'jordan@patelrealty.com', phone: '(720) 555-0101', role: 'Agent' },
   { id: 'u3', name: 'Riley Chen', email: 'riley@patelrealty.com', phone: '(303) 555-0102', role: 'Agent' },
   { id: 'u4', name: 'Casey Morgan', email: 'casey@patelrealty.com', role: 'Agent' },
   { id: 'u5', name: 'Taylor Brooks', email: 'taylor@patelrealty.com', phone: '(719) 555-0104', role: 'Admin' },
   { id: 'u6', name: 'Morgan Diaz', email: 'morgan@patelrealty.com', role: 'Agent' },
   { id: 'u7', name: 'Avery Kim', email: 'avery@patelrealty.com', phone: '(303) 555-0107', role: 'Agent' },
]

export const SEAT_FIRST = 97
export const SEAT_EXTRA = 50

/** 200 is the default. */
export const BUDGETS = [
   { id: 'starter', label: 'Starter', amount: 200, blurb: 'A steady start on new leads' },
   { id: 'growth', label: 'Growth', amount: 300, blurb: 'New leads plus a revival push' },
   { id: 'scale', label: 'Scale', amount: 500, blurb: 'Every lead, every day' },
] as const
export const DEFAULT_BUDGET = 200
export const leadsFor = (amount: number) => Math.round(amount * 1.86)

export const DEMO_BUSINESS = {
   legalBusinessName: 'Patel Realty Group LLC',
   friendlyBusinessName: 'Patel Realty',
   websiteUrl: 'https://patelrealty.com',
   city: 'Colorado Springs',
   region: 'CO',
   businessType: 'LLC',
}

/** Where the account's leads are, by area, from Follow Up Boss addresses. */
export interface IArea {
   code: string
   city: string
   state: string
   leads: number
}
export const LEAD_AREAS: IArea[] = [
   { code: '719', city: 'Colorado Springs', state: 'CO', leads: 1420 },
   { code: '303', city: 'Denver', state: 'CO', leads: 610 },
   { code: '720', city: 'Denver', state: 'CO', leads: 240 },
   { code: '970', city: 'Fort Collins', state: 'CO', leads: 95 },
   { code: '505', city: 'Albuquerque', state: 'NM', leads: 40 },
]
/** A searchable list of area codes beyond the account's own. */
export const AREA_DIRECTORY: Omit<IArea, 'leads'>[] = [
   ...LEAD_AREAS.map(({ code, city, state }) => ({ code, city, state })),
   { code: '616', city: 'Grand Rapids', state: 'MI' },
   { code: '602', city: 'Phoenix', state: 'AZ' },
   { code: '480', city: 'Scottsdale', state: 'AZ' },
   { code: '512', city: 'Austin', state: 'TX' },
   { code: '214', city: 'Dallas', state: 'TX' },
   { code: '305', city: 'Miami', state: 'FL' },
   { code: '206', city: 'Seattle', state: 'WA' },
   { code: '312', city: 'Chicago', state: 'IL' },
   { code: '404', city: 'Atlanta', state: 'GA' },
   { code: '702', city: 'Las Vegas', state: 'NV' },
]

export interface IStageOrTag {
   id: string
   name: string
   kind: 'stage' | 'tag'
}
export const DEMO_STAGES: IStageOrTag[] = ['New Lead', 'Attempted Contact', 'Nurture', 'Hot Prospect', 'Appointment Set', 'Appointment Missed', 'Active Client', 'Under Contract', 'Closed', 'Trash', 'Sphere', 'Past Client'].map((name) => ({ id: 's:' + name, name, kind: 'stage' as const }))
export const DEMO_TAGS: IStageOrTag[] = ['zillow', 'buyer', 'seller', 'investor', 'do-not-call', 'sold', 'has-agent', 'relocation', 'opted-out'].map((name) => ({ id: 't:' + name, name, kind: 'tag' as const }))
export const ALL_STAGES_AND_TAGS = [...DEMO_STAGES, ...DEMO_TAGS]

export type TStopGroup = 'working' | 'done' | 'never'
export const STOP_SUGGESTIONS: Record<TStopGroup, string[]> = {
   working: ['s:Active Client', 's:Under Contract', 's:Hot Prospect'],
   done: ['s:Closed', 's:Past Client', 't:sold', 't:has-agent'],
   never: ['s:Trash', 't:do-not-call', 't:opted-out'],
}
export const STOP_QUESTIONS: { id: TStopGroup; title: string; question: string; hint: string }[] = [
   { id: 'working', title: 'Leads you’re working', question: 'Which leads are you already working yourself?', hint: 'The stages or tags that mean “I’m on it”. We never call these leads.' },
   { id: 'done', title: 'Leads already done', question: 'Which leads are already done?', hint: 'Closed, sold, or with another agent. We leave these alone too.' },
   { id: 'never', title: 'Do not contact', question: 'Who must we never contact?', hint: 'Do-not-call, trash, opted out. Add anything else that should stop us.' },
]

export interface ISource {
   name: string
   newLeads: number
   total: number
}
export const DEMO_SOURCES: ISource[] = [
   { name: 'Zillow', newLeads: 84, total: 1240 },
   { name: 'Realtor.com', newLeads: 51, total: 620 },
   { name: 'Website', newLeads: 23, total: 310 },
   { name: 'Open house', newLeads: 9, total: 140 },
]
export const DEMO_PONDS = [{ id: 'p1', name: 'Buyer Pond', members: 312 }]

/** The voice orbs: the dashboard's gradient spheres, one palette each. */
export interface IVoice {
   id: string
   name: string
   tagline: string
   palette: [string, string, string]
}
export const VOICES: IVoice[] = [
   { id: 'dakotah', name: 'Dakotah', tagline: 'Warm and unhurried', palette: ['#7CF2B8', '#5DD0FF', '#8E7BFF'] },
   { id: 'bradward', name: 'Bradward', tagline: 'Clear and direct', palette: ['#5CE6FF', '#FF8ADB', '#8E7BFF'] },
   { id: 'maya', name: 'Maya', tagline: 'Bright and friendly', palette: ['#FFC46B', '#FF7AA2', '#B48CFF'] },
   { id: 'elena', name: 'Elena', tagline: 'Calm and precise', palette: ['#9EE8FF', '#7CF2B8', '#F6E27A'] },
]

/* ---------- Campaign filters (the composer's dropdowns) ---------- */

export type TFilterField = 'source' | 'age' | 'type' | 'stage' | 'tag' | 'pond'
export interface IFilter {
   field: TFilterField
   op: 'is' | 'not'
   value: string
}
export const FILTER_FIELDS: { id: TFilterField; label: string; ops: Array<'is' | 'not'>; values: () => string[] }[] = [
   { id: 'source', label: 'Lead source', ops: ['is', 'not'], values: () => DEMO_SOURCES.map((s) => s.name) },
   { id: 'age', label: 'Lead age', ops: ['is'], values: () => ['new, as they arrive', 'older than 30 days', 'older than 90 days', 'any age'] },
   { id: 'type', label: 'Lead type', ops: ['is'], values: () => ['buyers and sellers', 'buyers', 'sellers', 'renters'] },
   { id: 'stage', label: 'Stage', ops: ['is', 'not'], values: () => DEMO_STAGES.map((s) => s.name) },
   { id: 'tag', label: 'Tag', ops: ['is', 'not'], values: () => DEMO_TAGS.map((t) => t.name) },
   { id: 'pond', label: 'Pond', ops: ['is'], values: () => DEMO_PONDS.map((p) => p.name) },
]

export const filterSentence = (filters: IFilter[]) => {
   const part = (f: IFilter) => {
      switch (f.field) {
         case 'source':
            return `${f.op === 'not' ? 'not ' : ''}from ${f.value}`
         case 'age':
            return f.value
         case 'type':
            return f.value
         case 'stage':
            return `${f.op === 'not' ? 'not ' : ''}in ${f.value}`
         case 'tag':
            return `${f.op === 'not' ? 'not ' : ''}tagged ${f.value}`
         case 'pond':
            return `in ${f.value}`
      }
   }
   return filters.map(part)
}

export const countLeads = (filters: IFilter[]) => {
   const sources = filters.filter((f) => f.field === 'source' && f.op === 'is').map((f) => f.value)
   const age = filters.find((f) => f.field === 'age')?.value || 'new, as they arrive'
   const base = (sources.length ? sources : DEMO_SOURCES.map((s) => s.name)).reduce((n, name) => n + (DEMO_SOURCES.find((s) => s.name === name)?.[age.startsWith('new') ? 'newLeads' : 'total'] || 0), 0)
   const pond = filters.some((f) => f.field === 'pond') ? DEMO_PONDS[0].members : 0
   const type = filters.find((f) => f.field === 'type')?.value || 'buyers and sellers'
   const factor = (type === 'buyers and sellers' ? 1 : 0.55) * (age.includes('30') ? 0.5 : age.includes('90') ? 0.35 : age.startsWith('any') ? 1 : 1) * Math.pow(0.9, filters.filter((f) => f.op === 'not').length)
   return Math.max(1, Math.round((base + pond) * factor))
}

/** Words → filters. A small, honest keyword compiler for the demo. */
export const compileRequest = (text: string): { filters: IFilter[]; name: string } => {
   const t = text.toLowerCase()
   const filters: IFilter[] = []
   DEMO_SOURCES.forEach((s) => {
      if (t.includes(s.name.toLowerCase())) filters.push({ field: 'source', op: 'is', value: s.name })
   })
   DEMO_PONDS.forEach((p) => {
      if (t.includes(p.name.toLowerCase())) filters.push({ field: 'pond', op: 'is', value: p.name })
   })
   DEMO_STAGES.forEach((s) => {
      const n = s.name.toLowerCase()
      if (t.includes(n)) filters.push({ field: 'stage', op: t.includes('not in ' + n) || t.includes('not ' + n) ? 'not' : 'is', value: s.name })
   })
   DEMO_TAGS.forEach((g) => {
      if (t.includes('tagged ' + g.name) || t.includes(g.name + ' tag')) filters.push({ field: 'tag', op: 'is', value: g.name })
   })
   const m = t.match(/(\d+)\s*(days?|months?)/)
   const older = /older|cold|revive|backlog/.test(t) || Boolean(m)
   filters.push({ field: 'age', op: 'is', value: m ? (Number(m[1]) >= 60 || m[2].startsWith('month') ? 'older than 90 days' : 'older than 30 days') : older ? 'older than 90 days' : 'new, as they arrive' })
   const buyers = /buyer/.test(t)
   const sellers = /seller/.test(t)
   const renters = /rent/.test(t)
   filters.push({ field: 'type', op: 'is', value: renters ? 'renters' : buyers && !sellers ? 'buyers' : sellers && !buyers ? 'sellers' : 'buyers and sellers' })
   const name = text.trim() ? text.trim().charAt(0).toUpperCase() + text.trim().slice(1, 60) : 'Custom campaign'
   return { filters, name }
}

export interface IStarter {
   id: string
   title: string
   description: string
   leads: number
   kind: 'new' | 'older' | 'pond'
   filters: IFilter[]
   recommended?: boolean
}
export const STARTERS: IStarter[] = [
   { id: 'new-zillow', title: 'New Zillow leads', description: 'Call every new Zillow lead within 2 minutes of arrival and book the appointment for you.', leads: 84, kind: 'new', recommended: true, filters: [{ field: 'source', op: 'is', value: 'Zillow' }, { field: 'age', op: 'is', value: 'new, as they arrive' }, { field: 'type', op: 'is', value: 'buyers and sellers' }] },
   { id: 'revive', title: 'Revive cold leads', description: 'Work the older leads that never booked, a few each day, and hand you the ones that are ready again.', leads: 2170, kind: 'older', filters: [{ field: 'age', op: 'is', value: 'older than 90 days' }, { field: 'stage', op: 'not', value: 'Sphere' }, { field: 'type', op: 'is', value: 'buyers and sellers' }] },
   { id: 'pond', title: 'Buyer Pond leads', description: 'Call the leads sitting in your Buyer Pond that nobody has claimed yet.', leads: 312, kind: 'pond', filters: [{ field: 'pond', op: 'is', value: 'Buyer Pond' }, { field: 'age', op: 'is', value: 'any age' }, { field: 'type', op: 'is', value: 'buyers' }] },
]

export const EXAMPLE_REQUESTS = ['Call my Zillow leads older than 30 days', 'New Realtor.com buyers as they come in', 'Everyone in my Buyer Pond', 'Sellers tagged investor, not in Sphere']

/* ---------- Campaign stats (the dashboard after launch) ---------- */
export interface ICampaignStats {
   calls: number
   answered: number
   conversations: number
   appointments: number
   transfers: number
}
export const demoStatsFor = (leads: number, seed: number): ICampaignStats => {
   const calls = Math.round(leads * (0.6 + (seed % 5) * 0.08))
   const answered = Math.round(calls * 0.31)
   const conversations = Math.round(answered * 0.62)
   const appointments = Math.max(1, Math.round(conversations * 0.27))
   const transfers = Math.max(0, Math.round(conversations * 0.18))
   return { calls, answered, conversations, appointments, transfers }
}
